<template>
  <Form ref="formRef" @submit="onSubmit" v-slot="{ isSubmitting, isValidating, meta }" :initial-values="initialValues">
    <slot v-bind:invalid="!meta.valid" v-bind:loading="isSubmitting || isValidating" v-bind:success="success" v-bind:changed="meta.dirty"></slot>
  </Form>
</template>

<script lang="ts">
  import { Form, defineRule as extend } from "vee-validate";
  import { all as rules } from "@vee-validate/rules";
  import { i18n } from "@/localization";

  interface Rules {
    [key: string]: any;
  }
  const customRules: Rules = rules;
  Object.keys(customRules).forEach((rule: string) => {
    extend(rule, customRules[rule]);
  });

  for (const [rule, validation] of Object.entries(rules)) {
    if (rule === "required") {
      continue;
    }
    extend(rule, (value: string, validator, ctx: any) => {
      if (validation(value, validator)) {
        return true;
      }
      // @ts-ignore
      return `${i18n.global.t(`forms.${ctx.name.split(".").pop()}`)} is ongeldig.`;
    });
  }

  extend("required", (value: string | Array<unknown>, _validator, ctx: any) => {
    if (value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0)) {
      // @ts-ignore
      return `${i18n.global.t(`forms.${ctx.name.split(".").pop()}`)} is verplicht.`;
    }
    return true;
  });

  extend("url", (value: string) => {
    let pattern = new RegExp(
      "^(https?:\\/\\/)?" + // protocol
        "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // domain name
        "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
        "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
        "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
        "(\\#[-a-z\\d_]*)?$",
      "i",
    ); // fragment locator
    return pattern.test(value) ? true : "De opgegeven URL is niet geldig.";
  });

  extend("required_if", (value: string, [field, value2]: [string, string], ctx: any) => {
    const fieldValue = ctx.form[i18n.global.t(`forms.${field}`)];
    if ((value2 === "null" ? !fieldValue : fieldValue !== value2) && !value) {
      // @ts-ignore
      return `${i18n.global.t(`forms.${ctx.name.split(".").pop()}`)} is verplicht indien ${i18n.global.t(`forms.${field}`)} gelijk is aan ${value2}.`;
    }
    return true;
  });

  extend("double", (value: string) => {
    if (typeof value !== "number" && !value) {
      return true;
    }
    return /^[0-9]+(\.[0-9]+)?$/.test(value) ? true : "De waarde moet een getal zijn.";
  });

  extend("phone", (value: string) => {
    if (typeof value !== "string" || !value) {
      return true;
    }

    return /^\+{0,1}[0-9]{0,3}[0-9]{4,12}$/.test(value) ? true : "De waarde moet een geldig telefoonnummer zijn.";
  });

  extend("date-after-today", (value: string) => {
    if (!value) {
      return true;
    }
    return new Date(value) > new Date() ? true : "De waarde moet een datum na vandaag zijn.";
  });

  extend("capitalized", (value: string, _validator, ctx: any) => {
    if (!value) {
      return true;
    }
    return /^[A-Z]$/.test(value.at(0) || "") ? true : `De waarde moet beginnen met een hoofdletter.`;
  });

  const data = {
    success: false,
  };

  export default {
    components: {
      Form,
    },
    data: () => data,
    props: {
      submit: {
        type: Function,
        required: true,
      },
      initialValues: {
        type: Object,
        default: () => ({}),
      },
    },
    computed: {
      ref() {
        return this.$refs.formRef;
      },
    },
    methods: {
      async onSubmit(val: any, ctx: any) {
        await this.submit({ reset: ctx.resetForm });
        data.success = true;
      },
    },
  };
</script>
